import { useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PromosAPI from '../api/PromosAPI';
import { Settings } from '../store/reducers/AccountSlice';
import {
  B2B_INVITE_QUERY_PARAM,
  isB2bInviteOrdersPath,
  shouldFetchB2bInviteEligibility,
  shouldForceShowB2bInvite,
} from '../utils/b2bInvite';
import { canShowB2bInvite, markB2bInviteDismissed } from '../utils/b2bInviteStorage';

export function useB2bStoreInvite() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const settings: Settings = useSelector((state: any) => state.settings.value);
  const [isOpen, setIsOpen] = useState(false);

  const force = searchParams.get(B2B_INVITE_QUERY_PARAM) === '1';
  const tenantId = settings.tenantId;
  const gate = {
    isLoggedIn: settings.isLoggedIn,
    isConfigurationLoaded: settings.isConfigurationLoaded,
    force,
    onOrdersPage: isB2bInviteOrdersPath(location.pathname),
    cooldownAllowsShow: tenantId ? canShowB2bInvite(tenantId) : false,
  };

  useEffect(() => {
    if (shouldForceShowB2bInvite(gate)) {
      setIsOpen(true);
      return;
    }

    if (!tenantId || !shouldFetchB2bInviteEligibility(gate)) {
      return;
    }

    let cancelled = false;

    PromosAPI.getB2bStoreInviteEligibility()
      .then((response) => {
        if (!cancelled && response.data.eligible) {
          setIsOpen(true);
        }
      })
      .catch((error) => {
        console.error('Failed to load B2B store-invite eligibility', { tenantId, error });
      });

    return () => {
      cancelled = true;
    };
    // gate is rebuilt each render; depend on the primitive inputs instead.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    settings.isLoggedIn,
    settings.isConfigurationLoaded,
    settings.tenantId,
    force,
    location.pathname,
  ]);

  const close = (persistDismiss: boolean) => {
    setIsOpen(false);
    if (persistDismiss && tenantId && !force) {
      markB2bInviteDismissed(tenantId);
    }
  };

  return { isOpen, close, force };
}
